import { EvidencePicker } from './source-evidence';
import type { BotScope } from '@cryptology-assist/domain';
import type { JSX } from 'react';
import {
  generateUuidV7,
  SALES_TECHNIQUES,
  type SalesPlaybook,
} from '@cryptology-assist/domain';
import { Field } from './components';
import type { Copy } from './copy';

export function SalesPlaybookEditor({
  value,
  onChange,
  copy,
  scope,
}: {
  scope: BotScope;
  value: SalesPlaybook | undefined;
  onChange: (value: SalesPlaybook | undefined) => void;
  copy: Copy;
}): JSX.Element {
  const edit = (patch: Partial<SalesPlaybook>) => {
    if (value) onChange({ ...value, ...patch });
  };
  const techniques = {
    adaptive_listening: copy.salesListening,
    personal_imagery: copy.salesImagery,
    self_persuasion: copy.salesSelfPersuasion,
    meaningful_comparison: copy.salesComparison,
    objection_resolution: copy.salesObjections,
    credible_social_proof: copy.salesProof,
    real_opportunity: copy.salesOpportunity,
    concrete_planning: copy.salesPlanning,
  };
  const choices = (
    items: readonly { id: string; description: string }[],
    selected: string[],
    update: (ids: string[]) => void
  ) => (
    <div className="checks">
      {items.map((item) => (
        <label key={item.id}>
          <input
            type="checkbox"
            checked={selected.includes(item.id)}
            onChange={(event) =>
              update(
                event.target.checked
                  ? [...selected, item.id]
                  : selected.filter((id) => id !== item.id)
              )
            }
          />
          {item.description || copy.salesDescribeFirst}
        </label>
      ))}
    </div>
  );
  return (
    <section className="form-section">
      <h2>{copy.salesPlaybook}</h2>
      <p>{copy.salesPlaybookHelp}</p>
      {value && (
        <>
          <Field label={copy.salesPositioning}>
            <textarea
              required
              maxLength={600}
              value={value.positioning}
              onChange={(event) => edit({ positioning: event.target.value })}
            />
          </Field>
          <details className="playbook-section">
            <summary>
              {copy.salesMotivations}{' '}
              <span className="badge">{value.motivations.length}</span>
            </summary>
            {value.motivations.map((item, index) => (
              <div className="form-section" key={item.id}>
                <Field label={copy.salesMotivation}>
                  <input
                    required
                    maxLength={600}
                    value={item.description}
                    onChange={(event) =>
                      edit({
                        motivations: value.motivations.map((entry, i) =>
                          i === index
                            ? { ...entry, description: event.target.value }
                            : entry
                        ),
                      })
                    }
                  />
                </Field>
                <button
                  type="button"
                  onClick={() =>
                    edit({
                      motivations: value.motivations.filter(
                        (entry) => entry.id !== item.id
                      ),
                      benefits: value.benefits.map((entry) => ({
                        ...entry,
                        motivationIds: entry.motivationIds.filter(
                          (id) => id !== item.id
                        ),
                      })),
                      adviserOutcomes: value.adviserOutcomes.map((entry) => ({
                        ...entry,
                        motivationIds: entry.motivationIds.filter(
                          (id) => id !== item.id
                        ),
                      })),
                    })
                  }
                >
                  {copy.salesRemove}
                </button>
              </div>
            ))}
            <button
              type="button"
              disabled={value.motivations.length >= 12}
              onClick={() =>
                edit({
                  motivations: [
                    ...value.motivations,
                    { id: generateUuidV7(), description: '' },
                  ],
                })
              }
            >
              {copy.salesAddMotivation}
            </button>
          </details>
          <details className="playbook-section">
            <summary>
              {copy.salesConcerns}{' '}
              <span className="badge">{value.concerns.length}</span>
            </summary>
            {value.concerns.map((item, index) => (
              <div className="form-section" key={item.id}>
                <Field label={copy.salesConcern}>
                  <input
                    required
                    maxLength={600}
                    value={item.description}
                    onChange={(event) =>
                      edit({
                        concerns: value.concerns.map((entry, i) =>
                          i === index
                            ? { ...entry, description: event.target.value }
                            : entry
                        ),
                      })
                    }
                  />
                </Field>
                <button
                  type="button"
                  onClick={() =>
                    edit({
                      concerns: value.concerns.filter(
                        (entry) => entry.id !== item.id
                      ),
                      adviserOutcomes: value.adviserOutcomes.map((entry) => ({
                        ...entry,
                        concernIds: entry.concernIds.filter(
                          (id) => id !== item.id
                        ),
                      })),
                    })
                  }
                >
                  {copy.salesRemove}
                </button>
              </div>
            ))}
            <button
              type="button"
              disabled={value.concerns.length >= 12}
              onClick={() =>
                edit({
                  concerns: [
                    ...value.concerns,
                    { id: generateUuidV7(), description: '' },
                  ],
                })
              }
            >
              {copy.salesAddConcern}
            </button>
          </details>
          <details className="playbook-section">
            <summary>
              {copy.salesBenefits}{' '}
              <span className="badge">{value.benefits.length}</span>
            </summary>
            <p>{copy.salesBenefitsHelp}</p>
            {value.benefits.map((item, index) => {
              const update = (patch: Partial<typeof item>) =>
                edit({
                  benefits: value.benefits.map((entry, i) =>
                    i === index ? { ...entry, ...patch } : entry
                  ),
                });
              return (
                <div className="form-section" key={item.id}>
                  <Field label={copy.salesBenefitMeaning}>
                    <textarea
                      required
                      maxLength={600}
                      value={item.meaning}
                      onChange={(event) =>
                        update({ meaning: event.target.value })
                      }
                    />
                  </Field>
                  <EvidencePicker
                    scope={scope}
                    copy={copy}
                    source={item.source}
                    evidence={item.evidence}
                    onChange={(source, evidence) =>
                      update({ source, evidence })
                    }
                  />
                  <Field label={copy.salesBenefitKind}>
                    <select
                      value={item.kind}
                      onChange={(event) =>
                        update({ kind: event.target.value as typeof item.kind })
                      }
                    >
                      <option value="feature">{copy.salesFeature}</option>
                      <option value="social_proof">{copy.salesProof}</option>
                      <option value="scarcity">{copy.salesOpportunity}</option>
                    </select>
                  </Field>
                  <p>{copy.salesForMotivations}</p>
                  {choices(value.motivations, item.motivationIds, (ids) =>
                    update({ motivationIds: ids })
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      edit({
                        benefits: value.benefits.filter(
                          (entry) => entry.id !== item.id
                        ),
                      })
                    }
                  >
                    {copy.salesRemove}
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              disabled={value.benefits.length >= 30}
              onClick={() =>
                edit({
                  benefits: [
                    ...value.benefits,
                    {
                      id: generateUuidV7(),
                      motivationIds: [],
                      source: '',
                      evidence: '',
                      meaning: '',
                      kind: 'feature',
                    },
                  ],
                })
              }
            >
              {copy.salesAddBenefit}
            </button>
          </details>
          <details className="playbook-section">
            <summary>
              {copy.salesAdviserOutcomes}{' '}
              <span className="badge">{value.adviserOutcomes.length}</span>
            </summary>
            <p>{copy.salesAdviserOutcomesHelp}</p>
            {value.adviserOutcomes.map((item, index) => {
              const update = (patch: Partial<typeof item>) =>
                edit({
                  adviserOutcomes: value.adviserOutcomes.map((entry, i) =>
                    i === index ? { ...entry, ...patch } : entry
                  ),
                });
              return (
                <div className="form-section" key={item.id}>
                  <Field label={copy.salesCallValue}>
                    <textarea
                      required
                      maxLength={600}
                      value={item.description}
                      onChange={(event) =>
                        update({ description: event.target.value })
                      }
                    />
                  </Field>
                  <p>{copy.salesForMotivations}</p>
                  {choices(value.motivations, item.motivationIds, (ids) =>
                    update({ motivationIds: ids })
                  )}
                  <p>{copy.salesForConcerns}</p>
                  {choices(value.concerns, item.concernIds, (ids) =>
                    update({ concernIds: ids })
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      edit({
                        adviserOutcomes: value.adviserOutcomes.filter(
                          (entry) => entry.id !== item.id
                        ),
                      })
                    }
                  >
                    {copy.salesRemove}
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              disabled={value.adviserOutcomes.length >= 12}
              onClick={() =>
                edit({
                  adviserOutcomes: [
                    ...value.adviserOutcomes,
                    {
                      id: generateUuidV7(),
                      motivationIds: [],
                      concernIds: [],
                      description: '',
                    },
                  ],
                })
              }
            >
              {copy.salesAddOutcome}
            </button>
          </details>
          <details className="playbook-section">
            <summary>{copy.salesTechniques}</summary>
            <div className="checks">
              {SALES_TECHNIQUES.map((item) => (
                <label key={item.id}>
                  <input
                    type="checkbox"
                    checked={value.enabledTechniques.includes(item.id)}
                    onChange={(event) =>
                      edit({
                        enabledTechniques: event.target.checked
                          ? [...value.enabledTechniques, item.id]
                          : value.enabledTechniques.filter(
                              (id) => id !== item.id
                            ),
                      })
                    }
                  />
                  {techniques[item.id]}
                </label>
              ))}
            </div>
          </details>
          <Field label={copy.salesExcluded}>
            <textarea
              rows={3}
              value={value.excludedClaims.join('\n')}
              onChange={(event) =>
                edit({
                  excludedClaims: event.target.value
                    .split('\n')
                    .filter((line) => line.trim()),
                })
              }
            />
          </Field>
        </>
      )}
    </section>
  );
}
